import { useState } from "react";
import { Button, Field, Input, toast } from "@/ui";
import { inferCategory } from "./lib/categories";
import { suggestKeywords } from "./lib/keywords";
import { useEnsureSelfContact, useSaveNeed, useSaveService } from "./lib/hooks";
import s from "./network.module.css";

const NEED_CHIPS = ["An accountant", "A designer", "Intros to investors"];
const OFFER_CHIPS = ["Product advice", "Hiring help", "Introductions in my industry"];

function ChipField({ question, chips, value, onChange, placeholder }: { question: string; chips: string[]; value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="u-col" style={{ gap: 8 }}>
      <div className={s.onboardingQ}>{question}</div>
      <div className={s.chips}>
        {chips.map((c) => (
          <button key={c} type="button" className={s.chip} data-selectable="true" data-on={value === c ? "true" : undefined} onClick={() => onChange(value === c ? "" : c)}>
            {c}
          </button>
        ))}
      </div>
      <Field hint="One line is enough. You can add details later.">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={question} />
      </Field>
    </div>
  );
}

/** One onboarding step: a need and an offer, saved to the self contact (created from the session user when missing). */
export function NetworkOnboardingStep({ onDone }: { onDone: () => void }) {
  const [need, setNeed] = useState("");
  const [offer, setOffer] = useState("");
  const ensureSelf = useEnsureSelfContact();
  const saveNeed = useSaveNeed();
  const saveService = useSaveService();
  const busy = saveNeed.isPending || saveService.isPending;

  const save = async () => {
    const n = need.trim();
    const o = offer.trim();
    if (!n && !o) {
      onDone();
      return;
    }
    try {
      const contactId = await ensureSelf();
      if (n) await saveNeed.mutateAsync({ contactId, title: n, description: "", category: inferCategory(n), keywords: suggestKeywords(n), keywordsGeneratedByAi: false, visibility: "connections" });
      if (o) await saveService.mutateAsync({ contactId, title: o, shortDescription: "", category: inferCategory(o), keywords: suggestKeywords(o), keywordsGeneratedByAi: false, visibility: "connections", isActive: true });
      toast("Saved. Hub will look for matches as your network grows.", "success");
      onDone();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not save.", "danger");
    }
  };

  return (
    <div className={s.onboarding} data-testid="network-onboarding">
      <ChipField question="What do you need right now?" chips={NEED_CHIPS} value={need} onChange={setNeed} placeholder="Someone who can..." />
      <ChipField question="What can you offer?" chips={OFFER_CHIPS} value={offer} onChange={setOffer} placeholder="I can help with..." />
      <div className={`${s.small} ${s.muted}`}>Only your connections on Hub see them, until you change who can see them. Hub never contacts anyone for you.</div>
      <div className="u-row">
        <Button variant="primary" onClick={save} loading={busy}>
          {need.trim() || offer.trim() ? "Save and continue" : "Skip for now"}
        </Button>
      </div>
    </div>
  );
}
